import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fmtUGX, fmtNum } from "@/lib/format";
import { Building2, Store, Search, Plus, MapPin, Crosshair, ShieldAlert, Power, Pencil, ScrollText, Layers, ExternalLink } from "lucide-react";
import { toast } from "sonner";

import { loadGoogleMaps as loadMapsApi } from "@/lib/google-maps-loader";
import { MerchantCommandModal } from "@/components/merchant-command-modal";

type SchoolRow = { id: string; name: string; district: string|null; region: string|null; latitude: number|null; longitude: number|null;
  student_count: number; staff_count: number; subscription_plan: string; health_score: number; active: boolean;
  registration_no: string|null; phone: string|null; email: string|null; school_type: string|null; };

type MerchantRow = { id: string; name: string; school_id: string; category: string; active: boolean;
  latitude: number|null; longitude: number|null; address: string|null; lifecycle_status: string|null;
  contact_phone: string|null; contact_email: string|null; fee_rate_bps: number; };

const SCHOOL_COLOR = "#7B1416";
const MERCHANT_COLORS: Record<string, string> = {
  canteen: "#1f7a4a", bookshop: "#8B6F47", uniform: "#9333ea", transport: "#0ea5e9",
  pharmacy: "#B33A1A", marketplace: "#d97706", default: "#15803d",
};

function svgPin(fill: string, label: string) {
  // Simple data URL pin with letter
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='36' height='44' viewBox='0 0 36 44'>
    <path d='M18 0C8.6 0 1 7.5 1 16.7 1 28.5 18 44 18 44s17-15.5 17-27.3C35 7.5 27.4 0 18 0z' fill='${fill}' stroke='white' stroke-width='2'/>
    <circle cx='18' cy='17' r='9' fill='white'/>
    <text x='18' y='21' text-anchor='middle' font-family='system-ui,sans-serif' font-size='12' font-weight='700' fill='${fill}'>${label}</text>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

export function GeoIntelligence() {
  const qc = useQueryClient();
  const ref = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const schoolMarkersRef = useRef<any[]>([]);
  const merchantMarkersRef = useRef<any[]>([]);
  const dropPinMarkerRef = useRef<any>(null);
  const [mapReady, setMapReady] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [showSchools, setShowSchools] = useState(true);
  const [showMerchants, setShowMerchants] = useState(true);
  const [q, setQ] = useState("");
  const [planFilter, setPlanFilter] = useState("all");
  const [catFilter, setCatFilter] = useState("all");

  const [selSchool, setSelSchool] = useState<SchoolRow | null>(null);
  const [selMerchant, setSelMerchant] = useState<MerchantRow | null>(null);

  const [enrollKind, setEnrollKind] = useState<"school" | "merchant" | null>(null);
  const [pickingForKind, setPickingForKind] = useState<"school" | "merchant" | null>(null);
  const [pickedCoords, setPickedCoords] = useState<{ lat: number; lng: number } | null>(null);

  const { data: schools = [] } = useQuery({
    queryKey: ["geo-schools"],
    queryFn: async () => (await supabase.from("schools").select("*").order("name")).data as SchoolRow[] ?? [],
  });
  const { data: merchants = [] } = useQuery({
    queryKey: ["geo-merchants"],
    queryFn: async () => (await supabase.from("merchants").select("*").order("name")).data as MerchantRow[] ?? [],
  });

  const filteredSchools = useMemo(() => schools.filter(s => {
    if (!s.latitude || !s.longitude) return false;
    if (planFilter !== "all" && s.subscription_plan !== planFilter) return false;
    if (q && !`${s.name} ${s.district ?? ""} ${s.registration_no ?? ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [schools, q, planFilter]);

  const filteredMerchants = useMemo(() => merchants.filter(m => {
    if (!m.latitude || !m.longitude) return false;
    if (catFilter !== "all" && m.category !== catFilter) return false;
    if (q && !`${m.name} ${m.category} ${m.address ?? ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [merchants, q, catFilter]);

  // Init map
  useEffect(() => {
    let cancelled = false;
    loadMapsApi().then(() => {
      if (cancelled || !ref.current || !window.google) return;
      mapRef.current = new window.google.maps.Map(ref.current, {
        center: { lat: 1.3733, lng: 32.2903 },
        zoom: 7, mapTypeControl: false, streetViewControl: false, fullscreenControl: true,
        styles: [
          { elementType: "geometry", stylers: [{ color: "#f7f1e3" }] },
          { elementType: "labels.text.stroke", stylers: [{ color: "#f7f1e3" }] },
          { elementType: "labels.text.fill", stylers: [{ color: "#3b3a2f" }] },
          { featureType: "water", elementType: "geometry", stylers: [{ color: "#a8cdc4" }] },
          { featureType: "road", elementType: "geometry", stylers: [{ color: "#ece3cf" }] },
          { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#c4b69a" }] },
          { featureType: "poi", stylers: [{ visibility: "off" }] },
        ],
      });
      mapRef.current.addListener("click", (e: any) => {
        if (!pickingForKind) return;
        const lat = e.latLng.lat(); const lng = e.latLng.lng();
        setPickedCoords({ lat, lng });
        if (dropPinMarkerRef.current) dropPinMarkerRef.current.setMap(null);
        dropPinMarkerRef.current = new window.google.maps.Marker({
          map: mapRef.current, position: { lat, lng }, animation: window.google.maps.Animation.DROP,
          icon: { url: svgPin("#B33A1A", pickingForKind === "school" ? "S" : "M"), scaledSize: new window.google.maps.Size(36, 44) },
        });
        toast.success("Coordinates captured. Continue enrollment in the panel.");
        setEnrollKind(pickingForKind);
        setPickingForKind(null);
      });
      setMapReady(true);
    }).catch(e => setErr(String(e?.message ?? e)));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickingForKind]);

  // Render markers
  useEffect(() => {
    if (!mapReady || !window.google) return;
    schoolMarkersRef.current.forEach(m => m.setMap(null)); schoolMarkersRef.current = [];
    if (showSchools) for (const s of filteredSchools) {
      const m = new window.google.maps.Marker({
        map: mapRef.current, position: { lat: s.latitude!, lng: s.longitude! }, title: s.name,
        icon: { url: svgPin(SCHOOL_COLOR, "S"), scaledSize: new window.google.maps.Size(34, 42) },
      });
      m.addListener("click", () => { setSelMerchant(null); setSelSchool(s); });
      schoolMarkersRef.current.push(m);
    }
    merchantMarkersRef.current.forEach(m => m.setMap(null)); merchantMarkersRef.current = [];
    if (showMerchants) for (const mr of filteredMerchants) {
      const color = MERCHANT_COLORS[mr.category] ?? MERCHANT_COLORS.default;
      const m = new window.google.maps.Marker({
        map: mapRef.current, position: { lat: mr.latitude!, lng: mr.longitude! }, title: mr.name,
        icon: { url: svgPin(color, "M"), scaledSize: new window.google.maps.Size(30, 38) },
      });
      m.addListener("click", () => { setSelSchool(null); setSelMerchant(mr); });
      merchantMarkersRef.current.push(m);
    }
  }, [mapReady, filteredSchools, filteredMerchants, showSchools, showMerchants]);

  const suspendMerchant = useMutation({
    mutationFn: async (m: MerchantRow) => {
      const next = m.lifecycle_status === "suspended" ? "active" : "suspended";
      const { error } = await supabase.from("merchants").update({ lifecycle_status: next, active: next === "active" }).eq("id", m.id);
      if (error) throw error;
      return next;
    },
    onSuccess: (next) => { toast.success(next === "suspended" ? "Merchant suspended" : "Merchant reactivated"); qc.invalidateQueries({ queryKey: ["geo-merchants"] }); setSelMerchant(null); },
    onError: (e: any) => toast.error(e.message),
  });

  const toggleSchool = useMutation({
    mutationFn: async (s: SchoolRow) => {
      const { error } = await supabase.from("schools").update({ active: !s.active }).eq("id", s.id);
      if (error) throw error;
      return !s.active;
    },
    onSuccess: (active) => { toast.success(active ? "School reactivated" : "School suspended"); qc.invalidateQueries({ queryKey: ["geo-schools"] }); setSelSchool(null); },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="font-display text-2xl tracking-tight">Geo Intelligence</div>
          <div className="text-sm text-muted-foreground">National education network map — schools, merchants, and operations across Uganda.</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => { setPickingForKind("school"); toast.message("Click on the map to drop the school location."); }}>
            <Crosshair className="mr-2 h-4 w-4" /> Pick school spot
          </Button>
          <Button variant="outline" onClick={() => { setPickingForKind("merchant"); toast.message("Click on the map to drop the merchant location."); }}>
            <Crosshair className="mr-2 h-4 w-4" /> Pick merchant spot
          </Button>
          <Button onClick={() => setEnrollKind("school")} className="bg-[var(--forest)] hover:bg-[var(--forest)]/90">
            <Plus className="mr-2 h-4 w-4" /> Enroll school
          </Button>
          <Button onClick={() => setEnrollKind("merchant")} className="bg-[var(--terracotta)] hover:bg-[var(--terracotta)]/90">
            <Plus className="mr-2 h-4 w-4" /> Enroll merchant
          </Button>
        </div>
      </div>

      {/* Layer + filter bar */}
      <Card className="p-3">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2"><Layers className="h-4 w-4 text-muted-foreground" /><span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Layers</span></div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={showSchools} onCheckedChange={setShowSchools} /><span className="inline-flex h-2.5 w-2.5 rounded-full" style={{ background: SCHOOL_COLOR }} />Schools <span className="text-muted-foreground">({filteredSchools.length})</span></label>
          <label className="flex items-center gap-2 text-sm"><Switch checked={showMerchants} onCheckedChange={setShowMerchants} /><span className="inline-flex h-2.5 w-2.5 rounded-full" style={{ background: MERCHANT_COLORS.default }} />Merchants <span className="text-muted-foreground">({filteredMerchants.length})</span></label>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <div className="relative"><Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" /><Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, district, category…" className="h-9 w-64 pl-8" /></div>
            <Select value={planFilter} onValueChange={setPlanFilter}>
              <SelectTrigger className="h-9 w-36"><SelectValue placeholder="Plan" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All plans</SelectItem><SelectItem value="standard">Standard</SelectItem><SelectItem value="enterprise">Enterprise</SelectItem><SelectItem value="trial">Trial</SelectItem></SelectContent>
            </Select>
            <Select value={catFilter} onValueChange={setCatFilter}>
              <SelectTrigger className="h-9 w-40"><SelectValue placeholder="Merchant type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All merchants</SelectItem>
                <SelectItem value="canteen">Canteen</SelectItem>
                <SelectItem value="bookshop">Bookshop</SelectItem>
                <SelectItem value="uniform">Uniform shop</SelectItem>
                <SelectItem value="transport">Transport</SelectItem>
                <SelectItem value="pharmacy">Pharmacy / medical</SelectItem>
                <SelectItem value="marketplace">Marketplace</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Map */}
      <Card className="overflow-hidden">
        <div ref={ref} style={{ width: "100%", height: 620 }} className="bg-muted/30" />
        {!mapReady && !err && <div className="px-4 py-3 text-sm text-muted-foreground">Loading map…</div>}
        {err && <div className="px-4 py-3 text-sm text-destructive">Map error: {err}</div>}
      </Card>

      {/* Legend / picking banner */}
      {pickingForKind && (
        <Card className="border-[var(--terracotta)]/40 bg-[color-mix(in_oklab,var(--terracotta)_8%,transparent)] p-3 text-sm">
          <span className="font-medium">Pick mode active —</span> click anywhere on the map to drop a {pickingForKind} pin. {pickedCoords && <span className="text-muted-foreground">Captured: {pickedCoords.lat.toFixed(5)}, {pickedCoords.lng.toFixed(5)}</span>}
          <Button size="sm" variant="ghost" className="ml-3" onClick={() => setPickingForKind(null)}>Cancel</Button>
        </Card>
      )}

      {/* School details */}
      <Sheet open={!!selSchool} onOpenChange={(o) => !o && setSelSchool(null)}>
        <SheetContent className="w-[480px] overflow-y-auto sm:max-w-[480px]">
          {selSchool && <SchoolPanel s={selSchool} onSuspend={() => toggleSchool.mutate(selSchool)} />}
        </SheetContent>
      </Sheet>

      {/* Merchant Command Center modal */}
      <MerchantCommandModal
        merchant={selMerchant as any}
        open={!!selMerchant}
        onOpenChange={(o: boolean) => !o && setSelMerchant(null)}
        schoolName={selMerchant ? schools.find(s => s.id === selMerchant.school_id)?.name : undefined}
      />

      {/* Enrollment dialogs */}
      <Dialog open={enrollKind === "school"} onOpenChange={(o) => !o && setEnrollKind(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Building2 className="h-4 w-4" /> Enroll new school</DialogTitle></DialogHeader>
          <EnrollSchoolForm pickedCoords={pickedCoords} onPick={() => { setEnrollKind(null); setPickingForKind("school"); }} onDone={() => { setEnrollKind(null); setPickedCoords(null); if (dropPinMarkerRef.current) { dropPinMarkerRef.current.setMap(null); dropPinMarkerRef.current = null; } qc.invalidateQueries({ queryKey: ["geo-schools"] }); }} />
        </DialogContent>
      </Dialog>

      <Dialog open={enrollKind === "merchant"} onOpenChange={(o) => !o && setEnrollKind(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Store className="h-4 w-4" /> Enroll new merchant</DialogTitle></DialogHeader>
          <EnrollMerchantForm schools={schools} pickedCoords={pickedCoords} onPick={() => { setEnrollKind(null); setPickingForKind("merchant"); }} onDone={() => { setEnrollKind(null); setPickedCoords(null); if (dropPinMarkerRef.current) { dropPinMarkerRef.current.setMap(null); dropPinMarkerRef.current = null; } qc.invalidateQueries({ queryKey: ["geo-merchants"] }); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SchoolPanel({ s, onSuspend }: { s: SchoolRow; onSuspend: () => void }) {
  const { data: studentCount = 0 } = useQuery({
    queryKey: ["school-student-count", s.id],
    queryFn: async () => (await supabase.from("students").select("id", { count: "exact", head: true }).eq("school_id", s.id)).count ?? 0,
  });
  const { data: merchantCount = 0 } = useQuery({
    queryKey: ["school-merchant-count", s.id],
    queryFn: async () => (await supabase.from("merchants").select("id", { count: "exact", head: true }).eq("school_id", s.id)).count ?? 0,
  });
  return (
    <div className="space-y-4">
      <SheetHeader><SheetTitle className="flex items-center gap-2"><span className="inline-flex h-2.5 w-2.5 rounded-full" style={{ background: SCHOOL_COLOR }} />{s.name}</SheetTitle></SheetHeader>
      <div className="flex flex-wrap gap-1">
        <Badge variant="secondary" className="capitalize">{s.subscription_plan}</Badge>
        <Badge variant="outline">{s.school_type ?? "day"}</Badge>
        <Badge variant={s.active ? "default" : "destructive"}>{s.active ? "Active" : "Suspended"}</Badge>
        {s.district && <Badge variant="outline"><MapPin className="mr-1 h-3 w-3" />{s.district}</Badge>}
      </div>

      <Tabs defaultValue="profile">
        <TabsList className="grid w-full grid-cols-4"><TabsTrigger value="profile">Profile</TabsTrigger><TabsTrigger value="financial">Financial</TabsTrigger><TabsTrigger value="ops">Operations</TabsTrigger><TabsTrigger value="admin">Admin</TabsTrigger></TabsList>
        <TabsContent value="profile" className="space-y-2 text-sm">
          <Row k="Registration #" v={s.registration_no ?? "—"} />
          <Row k="Phone" v={s.phone ?? "—"} />
          <Row k="Email" v={s.email ?? "—"} />
          <Row k="Region" v={s.region ?? "—"} />
          <Row k="Coordinates" v={`${s.latitude?.toFixed(5)}, ${s.longitude?.toFixed(5)}`} />
        </TabsContent>
        <TabsContent value="financial" className="space-y-2 text-sm">
          <Row k="Health score" v={`${s.health_score}/100`} />
          <Row k="Students" v={fmtNum(studentCount)} />
          <Row k="Staff" v={fmtNum(s.staff_count)} />
          <Row k="Subscription" v={s.subscription_plan} />
        </TabsContent>
        <TabsContent value="ops" className="space-y-2 text-sm">
          <Row k="Merchants on site" v={fmtNum(merchantCount)} />
          <Row k="Boarding" v={s.school_type === "boarding" ? "Yes" : s.school_type === "mixed" ? "Mixed" : "Day"} />
        </TabsContent>
        <TabsContent value="admin" className="space-y-2">
          <Button variant="outline" className="w-full justify-start"><Pencil className="mr-2 h-4 w-4" /> Edit profile</Button>
          <Button variant="outline" className="w-full justify-start"><ScrollText className="mr-2 h-4 w-4" /> View audit log</Button>
          <Button variant="outline" className="w-full justify-start" onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${s.latitude},${s.longitude}`, "_blank")}><ExternalLink className="mr-2 h-4 w-4" /> Open in Google Maps</Button>
          <Button variant={s.active ? "destructive" : "default"} className="w-full justify-start" onClick={onSuspend}><Power className="mr-2 h-4 w-4" /> {s.active ? "Suspend school" : "Reactivate school"}</Button>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function MerchantPanel({ m, schools, onSuspend }: { m: MerchantRow; schools: SchoolRow[]; onSuspend: () => void }) {
  const school = schools.find(s => s.id === m.school_id);
  return (
    <div className="space-y-4">
      <SheetHeader><SheetTitle className="flex items-center gap-2"><span className="inline-flex h-2.5 w-2.5 rounded-full" style={{ background: MERCHANT_COLORS[m.category] ?? MERCHANT_COLORS.default }} />{m.name}</SheetTitle></SheetHeader>
      <div className="flex flex-wrap gap-1">
        <Badge variant="secondary" className="capitalize">{m.category}</Badge>
        <Badge variant={m.lifecycle_status === "suspended" ? "destructive" : m.active ? "default" : "outline"}>{m.lifecycle_status ?? (m.active ? "active" : "inactive")}</Badge>
        {school && <Badge variant="outline">{school.name}</Badge>}
      </div>

      <Tabs defaultValue="profile">
        <TabsList className="grid w-full grid-cols-4"><TabsTrigger value="profile">Profile</TabsTrigger><TabsTrigger value="financial">Financial</TabsTrigger><TabsTrigger value="compliance">Compliance</TabsTrigger><TabsTrigger value="actions">Actions</TabsTrigger></TabsList>
        <TabsContent value="profile" className="space-y-2 text-sm">
          <Row k="Address" v={m.address ?? "—"} />
          <Row k="Phone" v={m.contact_phone ?? "—"} />
          <Row k="Email" v={m.contact_email ?? "—"} />
          <Row k="School" v={school?.name ?? "—"} />
          <Row k="Coordinates" v={`${m.latitude?.toFixed(5)}, ${m.longitude?.toFixed(5)}`} />
        </TabsContent>
        <TabsContent value="financial" className="space-y-2 text-sm">
          <Row k="Fee rate" v={`${(m.fee_rate_bps / 100).toFixed(2)}%`} />
          <Row k="Settlement status" v="Connected" />
        </TabsContent>
        <TabsContent value="compliance" className="space-y-2 text-sm">
          <Row k="KYC" v="Verified" />
          <Row k="URA TIN" v="On file" />
          <Row k="Trading license" v="Uploaded" />
        </TabsContent>
        <TabsContent value="actions" className="space-y-2">
          <Button variant="outline" className="w-full justify-start"><ShieldAlert className="mr-2 h-4 w-4" /> Investigate fraud</Button>
          <Button variant="outline" className="w-full justify-start"><Pencil className="mr-2 h-4 w-4" /> Edit details</Button>
          <Button variant="outline" className="w-full justify-start" onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${m.latitude},${m.longitude}`, "_blank")}><ExternalLink className="mr-2 h-4 w-4" /> Open in Google Maps</Button>
          <Button variant={m.lifecycle_status === "suspended" ? "default" : "destructive"} className="w-full justify-start" onClick={onSuspend}><Power className="mr-2 h-4 w-4" /> {m.lifecycle_status === "suspended" ? "Reactivate merchant" : "Suspend merchant"}</Button>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return <div className="flex justify-between gap-2 border-b py-1.5"><span className="text-muted-foreground">{k}</span><span className="font-medium text-right">{v}</span></div>;
}

function EnrollSchoolForm({ pickedCoords, onPick, onDone }: { pickedCoords: { lat: number; lng: number } | null; onPick: () => void; onDone: () => void }) {
  const [name, setName] = useState("");
  const [district, setDistrict] = useState("");
  const [type, setType] = useState("day");
  const [plan, setPlan] = useState("standard");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [regNo, setRegNo] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!name) return toast.error("School name required");
    if (!pickedCoords) return toast.error("Pick the location on the map first");
    setBusy(true);
    const { error } = await supabase.from("schools").insert({
      name, district, school_type: type as any, subscription_plan: plan,
      latitude: pickedCoords.lat, longitude: pickedCoords.lng,
      phone: phone || null, email: email || null, registration_no: regNo || null,
      active: true, health_score: 80,
    } as never);
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success(`${name} enrolled on the network`);
    onDone();
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2 space-y-1.5"><Label>School name *</Label><Input value={name} onChange={e => setName(e.target.value)} placeholder="St Mary's Kitende" /></div>
        <div className="space-y-1.5"><Label>District</Label><Input value={district} onChange={e => setDistrict(e.target.value)} placeholder="Wakiso" /></div>
        <div className="space-y-1.5"><Label>School type</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="day">Day</SelectItem><SelectItem value="boarding">Boarding</SelectItem><SelectItem value="mixed">Mixed</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label>Plan</Label>
          <Select value={plan} onValueChange={setPlan}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="trial">Trial</SelectItem><SelectItem value="standard">Standard</SelectItem><SelectItem value="enterprise">Enterprise</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label>Registration #</Label><Input value={regNo} onChange={e => setRegNo(e.target.value)} /></div>
        <div className="space-y-1.5"><Label>Phone</Label><Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+2567…" /></div>
        <div className="col-span-2 space-y-1.5"><Label>Email</Label><Input value={email} onChange={e => setEmail(e.target.value)} type="email" /></div>
      </div>
      <Card className="border-dashed p-3">
        <div className="flex items-center justify-between text-sm">
          <div>{pickedCoords ? <span><MapPin className="mr-1 inline h-3 w-3 text-[var(--forest)]" /> {pickedCoords.lat.toFixed(5)}, {pickedCoords.lng.toFixed(5)}</span> : <span className="text-muted-foreground">Pick the location on the map.</span>}</div>
          <Button size="sm" variant="outline" onClick={onPick}><Crosshair className="mr-2 h-4 w-4" /> {pickedCoords ? "Re-pick" : "Pick on map"}</Button>
        </div>
      </Card>
      <DialogFooter><Button onClick={submit} disabled={busy} className="w-full">{busy ? "Enrolling…" : "Launch school on the network"}</Button></DialogFooter>
    </div>
  );
}

function EnrollMerchantForm({ schools, pickedCoords, onPick, onDone }: { schools: SchoolRow[]; pickedCoords: { lat: number; lng: number } | null; onPick: () => void; onDone: () => void }) {
  const [name, setName] = useState("");
  const [schoolId, setSchoolId] = useState("");
  const [category, setCategory] = useState("canteen");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [feeRate, setFeeRate] = useState("200");
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!name) return toast.error("Merchant name required");
    if (!schoolId) return toast.error("Pick a host school");
    if (!pickedCoords) return toast.error("Pick the location on the map first");
    setBusy(true);
    const { error } = await supabase.from("merchants").insert({
      name, school_id: schoolId, category: category as any,
      latitude: pickedCoords.lat, longitude: pickedCoords.lng, address: address || null,
      contact_phone: phone || null, contact_email: email || null,
      fee_rate_bps: Number(feeRate) || 200, active: true, lifecycle_status: "active",
    } as never);
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success(`${name} enrolled on the network`);
    onDone();
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2 space-y-1.5"><Label>Business name *</Label><Input value={name} onChange={e => setName(e.target.value)} placeholder="Kitende Canteen" /></div>
        <div className="space-y-1.5"><Label>Host school *</Label>
          <Select value={schoolId} onValueChange={setSchoolId}>
            <SelectTrigger><SelectValue placeholder="Select school" /></SelectTrigger>
            <SelectContent>{schools.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label>Type</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="canteen">Canteen</SelectItem>
              <SelectItem value="bookshop">Bookshop</SelectItem>
              <SelectItem value="uniform">Uniform shop</SelectItem>
              <SelectItem value="transport">Transport</SelectItem>
              <SelectItem value="pharmacy">Pharmacy / medical</SelectItem>
              <SelectItem value="marketplace">Marketplace</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2 space-y-1.5"><Label>Address</Label><Input value={address} onChange={e => setAddress(e.target.value)} /></div>
        <div className="space-y-1.5"><Label>Phone</Label><Input value={phone} onChange={e => setPhone(e.target.value)} /></div>
        <div className="space-y-1.5"><Label>Email</Label><Input value={email} onChange={e => setEmail(e.target.value)} type="email" /></div>
        <div className="space-y-1.5"><Label>Commission (bps)</Label><Input value={feeRate} onChange={e => setFeeRate(e.target.value)} /></div>
      </div>
      <Card className="border-dashed p-3">
        <div className="flex items-center justify-between text-sm">
          <div>{pickedCoords ? <span><MapPin className="mr-1 inline h-3 w-3 text-[var(--forest)]" /> {pickedCoords.lat.toFixed(5)}, {pickedCoords.lng.toFixed(5)}</span> : <span className="text-muted-foreground">Pick the location on the map.</span>}</div>
          <Button size="sm" variant="outline" onClick={onPick}><Crosshair className="mr-2 h-4 w-4" /> {pickedCoords ? "Re-pick" : "Pick on map"}</Button>
        </div>
      </Card>
      <DialogFooter><Button onClick={submit} disabled={busy} className="w-full">{busy ? "Enrolling…" : "Approve and launch merchant"}</Button></DialogFooter>
    </div>
  );
}
